import { faDollarSign, faSearch } from '@fortawesome/free-solid-svg-icons';
import React, { Fragment, useEffect, useState, useContext } from 'react'
import { useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Toast } from './../UI/toast/Toast';
import { Button, Card, CardBody, CardHeader, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import { checkUserToken } from '../../helpers/api/auth';
import { fetchCondominiumBanks } from '../../helpers/api/banks';
import { fetchMyAliquotsAsProperty, findOneDebtToPayAsPropertyDetails, markAsPaidDebtToPayAsProperty } from '../../helpers/api/finance';
import { CurrencyContext } from '../../context/Context';
import { SUCCESS_RESPONSE } from '../../helpers/codeResponses/codes';
import handleErrorMessage from '../../helpers/errors/handleErrorMessage';
import withRedirect from '../../helpers/hoc/withRedirect'
import useSWRFetchPagination from '../../helpers/hooks/useSWRFetchPagination';
import WizardError from '../auth/register/WizardError';
import ButtonIcon from '../common/ButtonIcon';
import Loader from '../common/Loader';
import PageHeader from '../common/PageHeader';
import FullPagination from '../common/pagination/FullPagination';
import SelectBankListItem from '../common/SelectBankListItem';
import { PreloadState } from './../UI/preloading/PreloadState';

const CreatePropertyPayment = ({ setRedirect, setRedirectUrl }) => {
    const { currency: curr } = useContext(CurrencyContext);
    const activeExchangeRate = Number(curr?.value ?? 0);

    const { id } = useParams();

    const { state: { amount, date, aliquot, total_paid } } = useLocation()

    const { selectedCondominium } = useSelector(state => state.condominium);
    const { token } = useSelector(state => state.auth.user);
    // eslint-disable-next-line
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bank, setBank] = useState(null);
    const [aliquots, setAliquots] = useState(null);
    const [debttopay, setDebttopay] = useState(null);
    const [selectedBankId, setSelectedBankId] = useState(null);
    const [collapseOne, collapseOneOpen] = useState(false)
    const [imagePreview, setImagePreview] = useState(null);
    const [totalProperties, setTotalProperties] = useState(0);
    const [total, setTotal] = useState(amount - total_paid);
    const [params, setParams] = useState({
        condominiumId: selectedCondominium,
        search: null
    })
    const [search, setSearch] = useState('');
    const { data, handler: { goToPage, nextPage, perPage, previousPage }, isLoading, meta } = useSWRFetchPagination(fetchCondominiumBanks, params);
    const { register, handleSubmit, formState: { isSubmitting, errors }, setValue, setError, clearErrors } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        shouldUnregister: false,
    });

    const [isPending, setIsPending] = useState(false);

    register('bank_id');
    register('voucher');
    register('reference');

    const save = async values => {
        const { bank_id, voucher, reference } = values;
        setIsPending(true);
        try {
            let formData = new FormData();
            formData.append('bank_id', bank_id);
            formData.append('reference', reference);
            formData.append('amount', total);
            if (activeExchangeRate) formData.append("rate", activeExchangeRate);

            if (voucher) formData.append('voucher', voucher);

            const { code } = await markAsPaidDebtToPayAsProperty(id, formData);

            if (code === SUCCESS_RESPONSE.CREATED) {
                collapseOneOpen(false);
                setRedirectUrl(`/dashboard/my/finance/topay/`);
                setRedirect(true);
                Toast.fire({
                    type: 'success',
                    title: `Pago emitido exitosamente, espere su aprobación.`,
                });
            }
        } catch (error) {
            handleErrorMessage(error);
        } finally {
            setIsPending(false);
        }
    }

    const handleImageChange = (event) => {
        if (event.target.files.length !== 0) {
            setImagePreview(URL.createObjectURL(event.target.files[0]));
            register('voucher');
            setValue('voucher', event.target.files[0])
        }
    }

    useEffect(() => {
        if (selectedBankId) {
            clearErrors('bank_id');
        } else {
            setError('bank_id', {
                message: 'El banco es requerido'
            })
        }
        // eslint-disable-next-line
    }, [selectedBankId])

    useEffect(() => {
        if (date) {
            const findOne = async () => {
                try {
                    const response = await checkUserToken(token);
                    const { data } = await findOneDebtToPayAsPropertyDetails(date);
                    setDebttopay(data);
                    setUser(response);
                    if (aliquot) {
                        const rep = await fetchMyAliquotsAsProperty();
                        setAliquots(rep.data);
                        if (rep.data) {
                            setTotal(amount - rep.data.value);
                        }
                    }
                    let totala = 0;
                    data.forEach((val) => { totala += val.price; })
                    setTotalProperties(totala / total);
                }
                catch (error) {
                    handleErrorMessage(error);
                } finally {
                    setLoading(false);
                }
            }
            findOne();
        }
        // eslint-disable-next-line
    }, [])

    const renderBanksItems = () => {
        return data.map((bank, i) => {
            return <SelectBankListItem key={i} id={bank.condominium_banks_id} selected={selectedBankId} onSelect={() => {
                setBank(bank);
                // condominium_banks_id: la cuenta de este banco EN este
                // condominio. bill_to_pays.bank_id referencia
                // condominium_banks.id, asi que mandar bank.bank_id
                // acreditaba el pago al banco de OTRO condominio.
                setSelectedBankId(bank.condominium_banks_id)
                setValue('bank_id', bank.condominium_banks_id);
            }} title={bank.name} alternativeName='banco' createdAt={bank.created_at} owner_dni={bank.owner_dni} owner_name={bank.owner_name} account={bank.account_number} />
        })
    }

    return (
        <Fragment>
            <PageHeader
                title="Emisión de pago" titleTag="h2"
                description={'Registra tu pago de la cuenta por pagar.'}
                className="mb-3" col={{ lg: 9 }}
            >
                <Button data-testid="condominium-button-chat" color="link" size="sm" className="pl-0" to="#!">
                    Tienes alguna pregunta? chatea con nosotros
                </Button>
            </PageHeader>
            <Card data-testid="form" tag={Form} className="bg-transparent shadow-none" onSubmit={handleSubmit(save)}>
                <PreloadState loading={isPending} message={'Emitiendo pago por favor espere...'}>
                    <>
                        <Row className='no-gutters'>
                            {!loading ? <>
                                <Col className='pr-lg-2 mb-3' lg={8}>
                                    <Card >
                                        <CardHeader>
                                            <Row>
                                                <Col>
                                                    <h5 className="mb-2 mb-md-0">Detalle de pago</h5>
                                                </Col>
                                            </Row>
                                        </CardHeader>
                                        <CardBody className="bg-light">
                                            <Row form>
                                                <Col>
                                                    <Row>
                                                        <Col tag={FormGroup}>
                                                            <Label>Seleccione un banco del condominio</Label>
                                                            <Row>
                                                                <Col tag={FormGroup} xs={9} className='pr-0'>
                                                                    <Input
                                                                        type='text'
                                                                        value={search}
                                                                        placeholder='Nombre, cuenta, titular, cédula, correo o moneda'
                                                                        onKeyDown={(event) => {
                                                                            if (event.key !== 'Enter') return;
                                                                            event.preventDefault();
                                                                            const value = event.currentTarget.value.trim();
                                                                            setSearch(value);
                                                                            setParams((prev) => ({ ...prev, search: value || null }));
                                                                        }}
                                                                        onChange={(event) => {
                                                                            const value = event.target.value;
                                                                            setSearch(value);
                                                                            if (value.trim() === '') setParams((prev) => ({ ...prev, search: null }));
                                                                        }}
                                                                    />
                                                                </Col>
                                                                <Col tag={FormGroup} xs={3}>
                                                                    <ButtonIcon className='btn-block' style={{ whiteSpace: 'nowrap' }} icon={faSearch} color='falcon-default' onClick={() => {
                                                                        const value = search.trim();
                                                                        setSearch(value);
                                                                        setParams((prev) => ({ ...prev, search: value || null }));
                                                                    }} >Buscar</ButtonIcon>
                                                                </Col>
                                                            </Row>
                                                            <Row>
                                                                <Col>
                                                                    {!isLoading ? data.length >= 1 ? renderBanksItems() : <p>No se han encontrado bancos.</p> : <Loader padding='py-3' />}
                                                                    <FullPagination
                                                                        nextPage={nextPage}
                                                                        prevPage={previousPage}
                                                                        goToPage={goToPage}
                                                                        meta={meta}
                                                                        className={'shadow-none'}
                                                                        posText={"bancos"}
                                                                        perPage={perPage} />
                                                                    <WizardError error={errors['bank_id']?.message} className="fs--1 font-weight-normal d-block" />
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                    <FormGroup>
                                                        <Label for="name">Nombre del banco</Label>
                                                        <Input
                                                            disabled
                                                            defaultValue={bank?.name || undefined}
                                                            autoComplete="off"
                                                            name="name"
                                                            id="title"
                                                            placeholder="Nombre del banco" />
                                                    </FormGroup>
                                                    <Row form>
                                                        <Col sm tag={FormGroup}>
                                                            <Label for="account_number">Número de cuenta</Label>
                                                            <NumberFormat
                                                                disabled
                                                                value={bank?.account_number || undefined}
                                                                format="#### #### #### #### #### #### ####"
                                                                placeholder="Número de cuenta"
                                                                className='form-control'
                                                                renderText={(value, props) => <div {...props}>{value}</div>} />

                                                        </Col>
                                                        <Col sm tag={FormGroup}>
                                                            <Label for="owner_name">Nombre del propietario</Label>
                                                            <Input
                                                                disabled
                                                                defaultValue={bank?.owner_name || undefined}
                                                                autoComplete="off"
                                                                name="owner_name" id="owner_name"
                                                                placeholder="Nombre del propietario"
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Row form>
                                                        <Col sm tag={FormGroup}>
                                                            <Label for="owner_dni">Identificación (DNI)</Label>
                                                            <Input
                                                                defaultValue={bank?.owner_dni || undefined}
                                                                id="owner_dni"
                                                                name="owner_dni"
                                                                disabled
                                                                type="text"
                                                                placeholder="Identificación"
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col tag={FormGroup}>
                                                            <Label>Comprobante de pago</Label>
                                                            <Input type='file' onChange={handleImageChange} name='voucher' id='voucher' placeholder='Adjunte comprobante de pago' className='form-control-file' accept="image/png, image/jpeg" />
                                                        </Col>
                                                    </Row>
                                                    {imagePreview && <Row form className='d-flex justify-content-center'>
                                                        <Col style={{ backgroundImage: `url(${imagePreview})`, backgroundPosition: 'center', backgroundSize: 'contain', height: '400px', width: '400px', maxHeight: '400px', maxWidth: '400px', backgroundRepeat: 'no-repeat' }}>

                                                        </Col>
                                                    </Row>}
                                                    <Row>
                                                        <Col tag={FormGroup}>
                                                            <Label>Código de referencia</Label>
                                                            <Input type='text' name='reference' id='reference' onChange={(e) => setValue('reference', e.target.value)} />
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col className='pl-lg-2 mb-3' lg={4}>
                                    <Card >
                                        <CardHeader>
                                            <Row>
                                                <Col>
                                                    <h5 className="mb-2 mb-md-0">Conceptos</h5>
                                                </Col>
                                            </Row>
                                        </CardHeader>
                                        <CardBody className="bg-light">
                                            {debttopay.map((debt, i) => {
                                                return (
                                                    <Row key={i}>
                                                        <Col>
                                                            <p className='fs--1'>
                                                                <span>{debt.finance.name}</span>
                                                            </p>
                                                            <p className='font-weight-semi-bold'><NumberFormat prefix='$ ' fixedDecimalScale decimalScale={2} displayType='text' thousandSeparator value={(debt.price) / totalProperties} /></p>
                                                        </Col>
                                                    </Row>
                                                )
                                            })}
                                            {aliquot ?
                                                <Row>
                                                    <Col>
                                                        <p className='fs--1'>
                                                            <span>{aliquots.description}</span>
                                                        </p>
                                                        <p className='font-weight-semi-bold'><NumberFormat prefix='$ ' fixedDecimalScale decimalScale={2} displayType='text' thousandSeparator value={aliquots.value} /></p>
                                                    </Col>
                                                </Row> : <></>}
                                            <hr />
                                            <p className='mb-0 font-weight-semi-bold'>Monto a pagar</p>
                                            <Row className='align-items-center py-2'>
                                                <Col>
                                                    <NumberFormat prefix='$ ' className='form-control' onValueChange={({ value }) => { setTotal(value) }} isAllowed={({ floatValue }) => floatValue <= (amount - total_paid) && floatValue >= 0} fixedDecimalScale decimalScale={2} thousandSeparator defaultValue={total} />
                                                </Col>
                                            </Row>
                                            <ButtonIcon color="primary" icon={faDollarSign} onClick={() => collapseOneOpen(!collapseOne)} className='btn-block'>Emitir pago</ButtonIcon>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </> : <Card className='col-12'><Loader padding='py-5' /></Card>}
                        </Row>
                        <Modal isOpen={collapseOne} data-testid="modal">
                            <ModalHeader>Confirmación</ModalHeader>
                            <ModalBody>
                                <p className="mb-0 font-weight-bold fs--1">¿Está seguro de que ingresó todos los datos correctamente? Esta información no puede ser cambiada posteriormente.
                                </p>
                            </ModalBody>
                            <ModalFooter>
                                <Button disabled={isPending} onClick={() => collapseOneOpen(!collapseOne)}>
                                    Cancelar
                                </Button>
                                <Button
                                    data-testid="submit-button"
                                    color="primary"
                                    type='submit'
                                    disabled={isPending}
                                    onClick={handleSubmit(save)}
                                >
                                    {isPending && <Spinner size="sm" className="mr-2" />}
                                    Sí, pagar
                                </Button>
                            </ModalFooter>
                        </Modal>
                    </>
                </PreloadState>
            </Card>
        </Fragment>
    )
}

export default withRedirect(CreatePropertyPayment);